import React, { useCallback, useEffect, useState } from "react";
import styled, { withTheme } from "styled-components";
import LabelledInput from "../../ui-library/LabelledInput/LabelledInput";
import PrimaryButton from "../../ui-library/Buttons/PrimaryButton/PrimaryButton";
import Checkbox from "../../ui-library/Checkbox/Checkbox";
import AlertUGNX from "../../ToastsAndAlerts/AlertUGNX";
import { AlertType } from "../../ToastsAndAlerts/constants";
import { CAREGIVER_RELATIONSHIP } from "../../../modules/constants";

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  @media (max-width: 576px) {
    padding-top: 2rem;
    margin-top: auto;
    .btn {
      width: 100%;
    }
  }
`;

const StyledCheckboxContainer = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 8px;

  span {
    font-weight: 500;
  }
`;

const EditCaregiver = (props) => {
  const {
    onSubmit,
    caregiver,
    hasError,
    alertMessage = "",
    clearAlertMessage,
  } = props;

  const [selectedCaregiver, setSelectedCaregiver] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [relationship, setRelationship] = useState(
    caregiver?.relationship || CAREGIVER_RELATIONSHIP.caregiver
  );
  const [reasonForChange, setReasonForChange] = useState("");
  const [validReasonForChange, setValidReasonForChange] = useState(false);
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (hasError) {
      setSubmitting(false);
    }
  }, [hasError]);

  useEffect(() => {
    setValidReasonForChange(
      reasonForChange.replace(/\.|,/g, "").trim() !== "" &&
        reasonForChange.length <= 450
    );
  }, [reasonForChange]);

  useEffect(() => {
    if (!selectedCaregiver) {
      setSelectedCaregiver({
        ...caregiver,
        id: caregiver.ugnxId,
      });
    }
  }, [caregiver]);

  useEffect(() => {
    if (selectedCaregiver?.relationship === relationship && message) {
      clearAlertMessage();
    }
  }, [relationship, selectedCaregiver, message]);

  useEffect(() => {
    setMessage(alertMessage);
  }, [alertMessage]);

  const submitForm = useCallback(() => {
    const formData = {
      ugnxId: selectedCaregiver.id,
      relationship: relationship,
      reasonForChange: reasonForChange,
    };

    setSubmitting(true);
    onSubmit(formData);
  }, [selectedCaregiver, onSubmit, relationship, reasonForChange]);

  const validateReason = () => {
    let reasonError;

    if (reasonForChange.replace(/\.|,/g, "").trim() === "") {
      reasonError = "Please provide a reason";
    } else if (reasonForChange.length > 450) {
      reasonError = "Please enter a reason with less than 450 characters";
    }

    setErrors({
      ...errors,
      reason: reasonError,
    });
  };

  const validateBeforeSubmit = (event) => {
    event.preventDefault();
    if (reasonForChange !== "") {
      submitForm();
    }
  };

  const disableSubmitButton = useCallback(() => {
    return (
      !validReasonForChange ||
      submitting ||
      !!message ||
      selectedCaregiver?.relationship === relationship
    );
  }, [
    validReasonForChange,
    submitting,
    message,
    selectedCaregiver,
    relationship,
  ]);

  const selectCheckbox = () => {
    setRelationship((prevValue) => {
      if (prevValue !== CAREGIVER_RELATIONSHIP.participant) {
        return CAREGIVER_RELATIONSHIP.participant;
      } else {
        return CAREGIVER_RELATIONSHIP.caregiver;
      }
    });
  };

  return (
    <FormContainer data-testid="edit-caregiver-form">
      <LabelledInput
        data-testid="username-input"
        className="mb-4"
        title="Caregiver ID"
        value={selectedCaregiver?.id || ""}
        type="text"
        readOnly={true}
      />

      <StyledCheckboxContainer className="mb-4">
        <Checkbox
          data-testid="checkbox"
          checked={relationship === CAREGIVER_RELATIONSHIP.participant}
          onClick={selectCheckbox}
          icon={true}
          square={true}
        />
        <span data-testid="checkbox-label">Participant</span>
      </StyledCheckboxContainer>

      {message && <AlertUGNX type={AlertType.WARNING}>{message}</AlertUGNX>}

      <LabelledInput
        title="Reason for change"
        type="text"
        data-testid="update-reason-input"
        hasError={!!errors.reason}
        errorMessage={errors.reason}
        value={reasonForChange}
        onChange={(e) => setReasonForChange(e.target.value)}
        onBlur={() => validateReason()}
      />
      <StyledFooter className="mt-4">
        <PrimaryButton
          data-testid="submit-caregiver"
          onClick={validateBeforeSubmit}
          disabled={disableSubmitButton()}
        >
          Save
        </PrimaryButton>
      </StyledFooter>
    </FormContainer>
  );
};

export default withTheme(EditCaregiver);
